import React, { Fragment, Suspense, useEffect, useRef, useState } from "react";
import styles from "./styles/ChatBox.module.css";
import ChatInputBox from "./ChatInputBox";
import { useAtomValue, useSetAtom } from "jotai";
import {
    canvasAtom,
    isSocketConnectedAtom,
    socketStatusAtom,
    userInputsAtom,
} from "./atoms";
import { Button, Form, Input, Progress, Steps, Spin, Typography } from "antd";
import {
    ArrowRightOutlined,
    DownOutlined,
    RightOutlined,
    ArrowDownOutlined,
    CheckCircleTwoTone,
    LoadingOutlined,
} from "@ant-design/icons";
import log from "cslog";
import { progressAtom, useSocket } from "./socket/SocketManager";
import PhotoButton from "../qforms/standard_elements/PhotoButton";
import { startCase } from "lodash";
import { getHeroIcon } from "../_helpers/heroicons";
import Lottie from "lottie-react";
import ReactMarkdown from "react-markdown";
// import CodeView from "../common/CodeView";

const CodeView = React.lazy(() => import("../common/CodeView"));
const { Text, Title } = Typography;
const MESSAGES = [
    {
        role: "user",
        type: "text",
        content: {
            text: "Hello there",
        },
    },
    {
        role: "ai",
        type: "text",
        content: {
            text: "Hello there! How can I assist you today?",
        },
    },
    {
        role: "user",
        type: "text",
        content: {
            text: "I need help with my account.",
        },
    },
    {
        role: "ai",
        type: "text",
        content: {
            text: "Can you provide some information about your account?",
        },
    },
    {
        role: "user",
        type: "image",
        content: {
            url: "https://plus.unsplash.com/premium_photo-1667311649552-2cfab63bdcfc",
        },
    },
    {
        role: "user",
        type: "text",
        content: {
            text: "I need help with my account. Can you provide some information?",
        },
    },
    {
        role: "ai",
        type: "image",
        content: {
            url: "https://cdn.stocksnap.io/img-thumbs/280h/enchanting-forest_1CFKSWRR5X.jpg",
        },
    },
    {
        role: "ai",
        type: "text",
        content: {
            text: "To log in, please provide your username and password.",
        },
    },
    {
        role: "ai",
        type: "action",
        content: {
            action: "Designing post",
        },
    },
    {
        role: "ai",
        type: "action",
        content: {
            action: "Uploading photo",
        },
    },
    {
        role: "ai",
        type: "text",
        content: {
            text: "Please share her image",
        },
    },
];

function ChatBox({
    message_list = [],
    wrapper_style = {},
    agent = "master",
    setAgent,
}) {
    const chatEndRef = useRef(null);
    const userInputs = useAtomValue(userInputsAtom);
    const progress = useAtomValue(progressAtom);

    const total = message_list.length;

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [message_list, userInputs?.enable]);

    return (
        <div className={styles.wrapper} style={wrapper_style}>
            <div className={styles.header_wrapper}>
                <ConnectionStatus />
            </div>
            {/* <pre>{JSON.stringify(progress, null, 2)}</pre> */}
            <div className={styles.message_list}>
                {message_list.map((message, index) => {
                    if (index < total - 1) {
                        if (
                            message.type === "action" ||
                            message.type === "error"
                        ) {
                            return <span key={index}></span>;
                        }
                    }
                    if (message.role === "user") {
                        return (
                            <Fragment key={index}>
                                <div
                                    key={index}
                                    className={styles.message_wrapper_user}
                                >
                                    <div className={styles.message_user}>
                                        {/* <div className={styles.message_text}>{message.text}</div> */}
                                        <OneMessage
                                            message={message}
                                            index={index}
                                            total={total}
                                        />
                                    </div>
                                </div>
                                {message?.attachments?.length > 0 && (
                                    <div
                                        style={{
                                            marginTop: "-10px",
                                        }}
                                    >
                                        {message?.attachments?.map(
                                            (attachment, index) => {
                                                return (
                                                    <div
                                                        key={index}
                                                        className={
                                                            styles.message_wrapper_user
                                                        }
                                                    >
                                                        <div
                                                            className={
                                                                styles.message_user
                                                            }
                                                        >
                                                            <OneMessage
                                                                message={{
                                                                    role: "user",
                                                                    type: attachment?.type,
                                                                    content: {
                                                                        url: attachment?.value,
                                                                    },
                                                                }}
                                                                key={index}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            }
                                        )}
                                    </div>
                                )}
                            </Fragment>
                        );
                    }
                    return (
                        <div key={index} className={styles.message_wrapper_ai}>
                            {/* <div className={styles.message_text}>{message.text}</div> */}
                            <div className={styles.message_ai}>
                                <OneMessage
                                    message={message}
                                    index={index}
                                    total={total}
                                />
                            </div>
                        </div>
                    );
                })}

                {userInputs?.enable && (
                    <div className={styles.message_wrapper_ai}>
                        <UserInputsNeeded data={userInputs?.data} agent={agent} />
                    </div>
                )}

                <div ref={chatEndRef} />
            </div>

            <div className={styles.input_box_wrapper}>
                <ChatInputBox
                    show_reset_button={true}
                    disabled={userInputs?.enable}
                    wrapper_style={{ padding: 0 }}
                    is_bottom={true}
                    agent={agent}
                    setAgent={setAgent}
                />
            </div>
        </div>
    );
}

export default ChatBox;

function OneMessage({ message, index, total }) {
    const { content } = message;
    const isLastMessage = index === total - 1;
    const progress = useAtomValue(progressAtom);

    switch (message.type) {
        case "text":
            return (
                <div className={styles.message_text}>
                    <div className={styles.markdown_content}>
                        <ReactMarkdown>{content.text}</ReactMarkdown>
                    </div>
                </div>
                // <div className={styles.message_text}>
                //     <div
                //         className={styles.markdown_content}
                //         dangerouslySetInnerHTML={{ __html: content.text }}
                //     />
                // </div>
            );
        case "image":
            return (
                <img
                    className={styles.message_image}
                    src={content.url}
                    alt="image"
                />
            );
        case "action":
            return (
                <div className={styles.message_action}>
                    <TextLoading text={content.action} />
                </div>
            );
        case "error":
            return <div className={styles.message_error}>{content.error}</div>;
        case "code":
            return (
                <div>
                    <MessageCodeView data={content} />
                </div>
            );
        case "canvas":
            return <CanvasCard data={content} />;
        // return <pre className={styles.message_code}>
        //     {JSON.stringify(content.code, null, 2)}
        // </pre>;
        default:
            return (
                <pre className={styles.message_text}>
                    {JSON.stringify(message, null, 2)}
                </pre>
            );
    }
}

const STATUS_MAPPING = {
    disconnected: "Disconnected from server",
    connected: "Connected to server",
    offline: "Server is offline",
};

function ConnectionStatus() {
    const isConnected = useAtomValue(isSocketConnectedAtom);
    const socket_status = useAtomValue(socketStatusAtom);

    return (
        <div className={isConnected ? styles.connected : styles.disconnected}>
            {/* {isConnected ? "Connected to server" : "Disconnected from server"} */}
            {STATUS_MAPPING[socket_status]}
        </div>
    );
}

function TextLoading({ text }) {
    const progress = useAtomValue(progressAtom);
    if (
        progress?.steps &&
        Array.isArray(progress.steps) &&
        progress.steps.length > 0 &&
        text.toLowerCase().includes(progress.task.toLowerCase())
    ) {
        return <TaskProgressBar text={text} progress={progress} />;
    }

    return <div className={styles.text_loading01}>{text}</div>;
}

function TaskProgressBar({ text, progress }) {
    const [currentStep, setCurrentStep] = useState(0);
    const [progressPercent, setProgressPercent] = useState(0);
    const steps = progress?.steps || ["Processing"];

    // Progress through steps with appropriate timing
    useEffect(() => {
        if (currentStep < steps.length - 1) {
            const timer = setTimeout(() => {
                setCurrentStep((prev) => prev + 1);
                setProgressPercent(0); // Reset progress for new step
            }, 2500 + Math.random() * 1000);

            return () => clearTimeout(timer);
        }
    }, [currentStep, steps.length]);

    // Animate progress within current step
    useEffect(() => {
        if (currentStep < steps.length) {
            const progressTimer = setInterval(() => {
                setProgressPercent((prev) => {
                    const increment = Math.random() * 10 + 3;
                    return Math.min(prev + increment, 95);
                });
            }, 150);

            return () => clearInterval(progressTimer);
        }
    }, [currentStep, steps.length]);
    const isComplete =
        currentStep === steps.length - 1 &&
            progressPercent >= 90 &&
            steps.length > 1
            ? currentStep > 0
            : false;

    return (
        <div className={styles.vertical_progress_container}>
            {/* <div className={styles.progress_header}>
                <Title level={5} className={styles.title}>
                    {progress?.task} Generation
                </Title>
                <div className={styles.progress_spinner}>
                    {isComplete ? (
                        <CheckCircleTwoTone twoToneColor="#52c41a" />
                    ) : (
                        <Spin indicator={<LoadingOutlined spin />} />
                    )}
                </div>
            </div> */}
            <Steps
                progressDot
                size="small"
                direction="vertical"
                current={currentStep}
                className={styles.steps}
                items={steps.map((label, index) => ({
                    title: (
                        <div>
                            <Text
                                className={`${styles.title} ${index === currentStep
                                    ? styles.text_loading02
                                    : ""
                                    }`}
                                strong={index === currentStep}
                            >
                                {label}
                            </Text>
                            {/* {index === currentStep && (
                                <Progress
                                    percent={progressPercent}
                                    size="small"
                                    status="active"
                                    showInfo={false}
                                    className={styles.innerProgress}
                                />
                            )} */}
                        </div>
                    ),
                }))}
            />
        </div>
    );
}

export function MessageCodeView({ data, default_show = false }) {
    const { language, message, code } = data;

    const [show, setShow] = useState(default_show);

    if (language !== "json" && language !== "document") {
        return <></>;
    }
    return (
        <div>
            <Button
                onClick={() => setShow(!show)}
                iconPosition="start"
                size="small"
            >
                {show ? "Hide Code" : `Show Code (${message})`}
                {show ? <DownOutlined /> : <RightOutlined />}
            </Button>
            {show && (
                <Suspense fallback={<div>Loading...</div>}>
                    <CodeView
                        language={language}
                        code={JSON.stringify(data, null, 2)}
                    />
                </Suspense>
            )}
        </div>
    );
}

function UserInputsNeeded({ data, agent = "master" }) {
    const [form] = Form.useForm();
    const { isLoading, sendHumanReply } = useSocket();

    return (
        <div>
            {/* <hr /> */}
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            <Form
                form={form}
                layout="vertical"
                onFinish={(values) => {
                    log.d(values, "Submitting User Inputs");
                    sendHumanReply(values, agent);
                }}
            >
                {data?.inputs?.map((input, index) => {
                    return (
                        <Form.Item
                            key={index}
                            name={input.key}
                            label={
                                input.question ||
                                input.label ||
                                startCase(input.key)
                            }
                        >
                            {input.type === "text" && <Input />}
                            {input.type === "textarea" && <Input.TextArea />}
                            {input.type === "image" && (
                                <PhotoButton
                                    field={{
                                        collection: "qqq",
                                        field: input.key,
                                    }}
                                    width="150px"
                                    height="150px"
                                    objectFit="contain"
                                    show_placeholder={true}
                                    background="white"
                                    enable_remove_bg={false}
                                // onChangeFull={photo_obj => {
                                //     setImgValues({
                                //         ...img_values,
                                //         [input.key]: photo_obj
                                //     })
                                // }}
                                />
                            )}
                        </Form.Item>
                    );
                })}

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        iconPosition="start"
                        loading={isLoading}
                    >
                        Submit
                        <ArrowRightOutlined />
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}

function CanvasCard({ data }) {
    const setCanvas = useSetAtom(canvasAtom);
    console.log("data in canvas", data);
    if (data?.type == "blank") {
        return;
    }
    const config = getCanvasCardConfig(data?.type);
    // const config = getCanvasCardConfig("telegram_post");

    return (
        <div
            className={styles.message_canvas}
            // style={{ backgroundColor: config?.bg }}
            onClick={() => {
                setCanvas({
                    show: true,
                    data: data,
                });
            }}
        >
            {/* <div className={styles.lottieWrapper}>
                {animationData && (
                    <Lottie
                        lottieRef={playerRef}
                        animationData={animationData}
                        autoplay
                        loop={false}
                        style={{ height: "100px", width: "100px" }}
                    />
                )}
            </div> */}
            <div className={styles.icon} style={{ background: config?.bg }}>
                {config.icon_type === "hero" &&
                    getHeroIcon(config?.icon, {
                        width: "28px",
                        color: config?.color,
                    })}
                {config.icon_type === "fa" && (
                    <i
                        className={config?.icon}
                        style={{ fontSize: "28px", color: config?.color }}
                    />
                )}
            </div>
            {/* <h3 className={styles.headline}>
                Your {startCase(data.type)} is ready!
            </h3>
            <p className={styles.subtext}>All done. You focus on sharing.</p>
            <div className={styles.cta}>Read Now →</div> */}
            {/* <div>
                <div className={styles.title}>
                    Your {startCase(data.type)} is generated
                </div>
                <div className={styles.description}>Want a look?</div>
            </div> */}
            {/* <pre>{JSON.stringify(data, null, 2)}</pre> */}
            <div className={styles.canvas_content}>
                <div className={styles.title}>
                    {config.title} generated successfully!
                </div>
                <div className={styles.description}>{config.description}</div>
            </div>
            <div className={styles.canvas_arrow}>
                <ArrowRightOutlined />
            </div>
        </div>
    );
}

const CANVAS_CARD_MAPPING = {
    design: {
        icon: "photo",
        icon_type: "hero",
        color: "white",
        bg: "linear-gradient(135deg, #6EE7B7 0%, #3B82F6 50%, #9333EA 100%)",
        title: "Design",
        description:
            "Your visually striking design is ready to make an impression.",
    },
    blog: {
        icon: "document-text",
        icon_type: "hero",
        color: "white",
        bg: "linear-gradient(135deg,rgb(193, 69, 102) 0%,rgb(237, 217, 36) 100%)",
        title: "Blog Post",
        description:
            "Your thoughtful article is ready to inform and educate readers.",
    },
    linkedin_post: {
        icon: "fab fa-linkedin",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #0077b5 0%, #005885 100%)",
        title: "LinkedIn Post",
        description:
            "Your professional post is ready to publish and engage your network.",
    },
    facebook_post: {
        icon: "fab fa-facebook-f",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #1877f2 0%, #0c63d4 50%, #084baa 100%)",
        title: "Facebook Post",
        description: "Your engaging content is ready to reach your community.",
    },
    twitter_post: {
        icon: "fab fa-x-twitter",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #000000 0%, #1a1a1a 50%, #333333 100%)",
        title: "Twitter Post",
        description:
            "Your concise and impactful tweet is ready to spark conversations.",
    },
    instagram_post: {
        icon: "fab fa-instagram",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #feda75 0%, #fa7e1e 35%, #d62976 65%, #962fbf 100%)",
        title: "Instagram Post",
        description:
            "Your eye-catching post is ready to inspire and engage your audience.",
    },

    pinterest_post: {
        icon: "fab fa-pinterest-p",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #bd081c 0%,rgb(209, 16, 39) 100%)",
        title: "Pinterest Pin",
        description:
            "Your pin is ready to capture attention and drive inspiration.",
    },

    reddit_post: {
        icon: "fab fa-reddit-alien",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #ff4500 0%, #c33600 100%)",
        title: "Reddit Post",
        description:
            "Your discussion-starting post is ready to hit the front page.",
    },

    quora_post: {
        icon: "fab fa-quora",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #aa2200 0%, #881900 100%)",
        title: "Quora Answer",
        description:
            "Your thoughtful answer is ready to spark meaningful conversations.",
    },

    threads_post: {
        icon: "fab fa-threads", // Replace with a custom or SVG icon if unavailable in FA
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #000000 0%, #2e2e2e 100%)",
        title: "Threads Post",
        description:
            "Your real-time post is ready to engage the conversation flow.",
    },

    youtube_post: {
        icon: "fab fa-youtube",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #ff0000 0%, #cc0000 100%)",
        title: "YouTube Video",
        description:
            "Your video content is ready to entertain and inform viewers.",
    },

    telegram_post: {
        icon: "fab fa-telegram-plane",
        icon_type: "fa",
        color: "white",
        bg: "linear-gradient(135deg, #0088cc 0%, #006eaa 100%)",
        title: "Telegram Post",
        description:
            "Your update is ready to broadcast to your Telegram community.",
    },
};

function getCanvasCardConfig(type) {
    return (
        CANVAS_CARD_MAPPING?.[type] || {
            icon: "file",
            color: "royalblue",
            bg: "aliceblue",
        }
    );
}
